/**
 * UPT-2079 L14a (web §5.3, rest §2.1 n° 3 et §4.6) — THÈME D'UN MODULE GÉNÉRÉ : une donnée (couleurs hexadécimales résolues pour les deux modes, polices de la
 * liste fermée, formes par paliers) que le socle applique en redéfinissant une LISTE FERMÉE de variables CSS sur l'enveloppe du contenu des pages du module
 * (`ModuleThemeScope`). Aucune variable d'état (`success`, `warning`, `danger`, `info`), du mode Géode (`--geode-*`), des graphiques (`--portal-chart-*`) ni de
 * DS-0 (`--c-*`) n'y figure jamais.
 *
 * Ce fichier porte aussi le MIROIR TYPESCRIPT de la résolution des deux palettes du rest (§4.6 : valeurs Géode par mode, dérivations, corrections de contraste
 * nommées) : le panneau « Habillage » (rapport de contraste), le dépôt de démonstration et la simulation s'en servent ; les couleurs d'état n'y existent pas.
 * Fonctions pures, sans DOM.
 */
import type { CSSProperties } from 'react';
import { contrast_ratio, wcag_aa_large_text, wcag_aa_normal_text } from './theme_contrast';
import { css_color_to_hsl_channels } from './theme_css_variables';
import { COMPONENT_RECIPE_VARIABLES, componentRecipeVariables, type ComponentRecipes } from './component_recipes';
import { typographyVariables, type MeasuredTypography } from './typography_roles';
import { fontStack, type ModuleFontFamily } from './module_fonts';

export type ModuleThemeColors = {
  accent: string;
  onAccent: string;
  accentSoft: string;
  surface: string;
  surfaceRaised: string;
  surfaceMuted: string;
  ink: string;
  inkMuted: string;
  border: string;
};

export type ModuleThemeScheme = 'light' | 'dark';

export type ModuleThemeAdjustment = { scheme: ModuleThemeScheme; slot: string; from: string; to: string; rule: string };

export type GeneratedModuleTheme = {
  version: 1;
  moduleCode: string;
  applyTo: 'all' | 'custom-pages';
  baseScheme: ModuleThemeScheme;
  light: ModuleThemeColors;
  dark: ModuleThemeColors;
  components?: ComponentRecipes;
  typography: MeasuredTypography & {
    heading?: { family: ModuleFontFamily; weights: number[] };
    body?: { family: ModuleFontFamily; weights: number[] };
  };
  shape: {
    radius: 'none' | 'sm' | 'md' | 'lg' | 'xl';
    shadow: 'none' | 'sm' | 'md' | 'lg';
    density: 'compact' | 'comfortable' | 'spacious';
    cardStyle: 'flat' | 'outlined' | 'elevated' | 'glass';
  };
  adjustments: ModuleThemeAdjustment[];
};

/** Surcharge du locataire (L14d). */
export type ModuleThemeOverride = { mode: 'module' | 'tenant'; accentLight: string | null; accentDark: string | null };

/* ── Couleurs ────────────────────────────────────────────────────────────────────────────────────────────────────── */

const HEX = /^#[0-9a-f]{6}$/;

export const isModuleThemeHex = (value: unknown): value is string => typeof value === 'string' && HEX.test(value);

const channelsOf = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

const toHex = (channels: readonly number[]): string =>
  `#${channels.map((channel) => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0')).join('')}`;

/** `mix(a, b, t)` du rest §4.5 : mélange sRGB arrondi à l'entier (`t = 0` → `a`, `t = 1` → `b`). */
export const mixHex = (a: string, b: string, t: number): string => {
  const from = channelsOf(a);
  const to = channelsOf(b);
  return toHex(from.map((channel, index) => channel + (to[index] - channel) * t));
};

const ratio = (a: string, b: string): number => contrast_ratio(a, b) ?? 1;

/** Contraste arrondi à deux décimales pour l'affichage (« #777777 / #ffffff = 4.48 »). */
export const displayRatio = (value: number): number => Math.round(value * 100) / 100;

export const BLACK = '#000000';
export const WHITE = '#ffffff';

/** Noir ou blanc : celui qui contraste le mieux avec TOUS les fonds donnés (maximise le plus mauvais contraste). */
export const bestInkFor = (backgrounds: readonly string[]): string => {
  const worst = (ink: string) => Math.min(...backgrounds.map((background) => ratio(ink, background)));
  return worst(BLACK) >= worst(WHITE) ? BLACK : WHITE;
};

export const luminanceOf = (hex: string): number => {
  const [r, g, b] = channelsOf(hex).map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/* ── Valeurs du thème Géode par mode (rest §4.4 `geodeDefaults`) ─────────────────────────────────────────────────── */

export type GeodeModeDefaults = { surface: string; surfaceRaised: string; ink: string; inkMuted: string; border: string; accent: string };

/**
 * Valeurs du thème Géode par mode, relevées dans `app/design-system/tokens.css` et `semantic.css` : surface = `--portal-bg` (neutral-10), surface relevée =
 * `--portal-surface` (clair : neutral-11, sombre : neutral-7), encre = `--portal-ink` (neutral-0), encre secondaire = `--portal-ink-muted` (neutral-4),
 * bordure = `--portal-border` (neutral-6), accent = `--portal-accent` (primary-5). Le test `theme_module_theme` les RECALCULE depuis les feuilles.
 */
export const GEODE_DEFAULTS: Readonly<Record<ModuleThemeScheme, GeodeModeDefaults>> = {
  light: { surface: '#f5f8f9', surfaceRaised: '#ffffff', ink: '#1a2023', inkMuted: '#5b6a71', border: '#8d9da5', accent: '#18bf95' },
  dark: { surface: '#131c20', surfaceRaised: '#3b484e', ink: '#ffffff', inkMuted: '#8ea0a9', border: '#53626a', accent: '#3fd5af' },
};

/* ── Résolution des deux palettes (rest §4.6) ────────────────────────────────────────────────────────────────────── */

export type ModuleThemePaletteInput = {
  accent: string;
  onAccent?: string;
  accentSoft?: string;
  surface?: string;
  surfaceRaised?: string;
  ink?: string;
  inkMuted?: string;
  border?: string;
};

export type ModuleThemeResolveInput = {
  baseScheme?: ModuleThemeScheme;
  palette?: ModuleThemePaletteInput | null;
  paletteAlt?: ModuleThemePaletteInput | null;
};

export type ResolvedModuleThemeColors = { light: ModuleThemeColors; dark: ModuleThemeColors; adjustments: ModuleThemeAdjustment[] };

/** Les six paires du rapport de contraste (rest §4.6 n° 3) avec leur seuil. */
export const CONTRAST_PAIRS = [
  { id: 'ink/surface', a: 'ink', b: 'surface', min: wcag_aa_normal_text },
  { id: 'ink/surfaceRaised', a: 'ink', b: 'surfaceRaised', min: wcag_aa_normal_text },
  { id: 'inkMuted/surface', a: 'inkMuted', b: 'surface', min: wcag_aa_normal_text },
  { id: 'inkMuted/surfaceRaised', a: 'inkMuted', b: 'surfaceRaised', min: wcag_aa_normal_text },
  { id: 'accent/surface', a: 'accent', b: 'surface', min: wcag_aa_large_text },
  { id: 'onAccent/accent', a: 'onAccent', b: 'accent', min: wcag_aa_normal_text },
] as const;
export type ContrastPairId = (typeof CONTRAST_PAIRS)[number]['id'];

/** Couleurs d'un mode AVANT correction : clés absentes = valeurs Géode du mode ; `surfaceMuted` et `accentSoft` dérivés (rest §4.6 n° 1). */
export const buildModeColors = (scheme: ModuleThemeScheme, palette: Partial<ModuleThemePaletteInput> | null | undefined): ModuleThemeColors => {
  const defaults = GEODE_DEFAULTS[scheme];
  const surface = palette?.surface ?? defaults.surface;
  const surfaceRaised = palette?.surfaceRaised ?? defaults.surfaceRaised;
  const ink = palette?.ink ?? defaults.ink;
  const accent = palette?.accent ?? defaults.accent;
  return {
    accent,
    onAccent: palette?.onAccent ?? bestInkFor([accent]),
    accentSoft: palette?.accentSoft ?? mixHex(surface, accent, 0.14),
    surface,
    surfaceRaised,
    surfaceMuted: mixHex(surfaceRaised, ink, 0.06),
    ink,
    inkMuted: palette?.inkMuted ?? defaults.inkMuted,
    border: palette?.border ?? defaults.border,
  };
};

const STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export type CorrectionRule = 'a' | 'b' | 'c' | 'd';

/**
 * Corrections de contraste (rest §4.6 n° 3), dans cet ordre ; chacune est nommée dans `adjustments`. (a) encre / surfaces < 4,5 → noir ou blanc ; (b) encre
 * secondaire < 4,5 → mélange vers l'encre par pas de 0,1 ; (c) accent / surface < 3 → mélange vers l'encre ; (d) encre sur accent < 4,5 → noir ou blanc, puis
 * l'accent est mélangé vers l'inverse. Aucune couleur d'état n'existe. `only` limite les règles (la surcharge du locataire n'applique que (c) et (d)).
 */
export const correctModeColors = (
  scheme: ModuleThemeScheme,
  input: ModuleThemeColors,
  only?: readonly CorrectionRule[]
): { colors: ModuleThemeColors; adjustments: ModuleThemeAdjustment[] } => {
  const colors = { ...input };
  const adjustments: ModuleThemeAdjustment[] = [];
  const enabled = (rule: CorrectionRule) => only === undefined || only.includes(rule);
  const record = (slot: string, from: string, to: string, rule: string) => {
    if (from !== to) adjustments.push({ scheme, slot, from, to, rule });
  };

  if (enabled('a') && (ratio(colors.ink, colors.surface) < wcag_aa_normal_text || ratio(colors.ink, colors.surfaceRaised) < wcag_aa_normal_text)) {
    const from = colors.ink;
    colors.ink = bestInkFor([colors.surface, colors.surfaceRaised]);
    record('ink', from, colors.ink, 'ink-readable');
  }
  if (
    enabled('b') &&
    (ratio(colors.inkMuted, colors.surface) < wcag_aa_normal_text || ratio(colors.inkMuted, colors.surfaceRaised) < wcag_aa_normal_text)
  ) {
    const from = colors.inkMuted;
    let next = colors.inkMuted;
    for (const step of STEPS) {
      next = mixHex(from, colors.ink, step / 10);
      if (ratio(next, colors.surface) >= wcag_aa_normal_text && ratio(next, colors.surfaceRaised) >= wcag_aa_normal_text) break;
    }
    colors.inkMuted = next;
    record('inkMuted', from, next, 'ink-muted-readable');
  }
  if (enabled('c') && ratio(colors.accent, colors.surface) < wcag_aa_large_text) {
    const from = colors.accent;
    let next = from;
    for (const step of STEPS) {
      next = mixHex(from, colors.ink, step / 10);
      if (ratio(next, colors.surface) >= wcag_aa_large_text) break;
    }
    colors.accent = next;
    record('accent', from, next, 'accent-visible');
  }
  if (enabled('d') && ratio(colors.onAccent, colors.accent) < wcag_aa_normal_text) {
    const fromInk = colors.onAccent;
    colors.onAccent = bestInkFor([colors.accent]);
    record('onAccent', fromInk, colors.onAccent, 'on-accent-readable');
    if (ratio(colors.onAccent, colors.accent) < wcag_aa_normal_text) {
      const fromAccent = colors.accent;
      const toward = colors.onAccent === BLACK ? WHITE : BLACK;
      let next = fromAccent;
      for (const step of STEPS) {
        next = mixHex(fromAccent, toward, step / 10);
        if (ratio(colors.onAccent, next) >= wcag_aa_normal_text) break;
      }
      colors.accent = next;
      record('accent', fromAccent, next, 'accent-on-accent');
    }
  }
  return { colors, adjustments };
};

/**
 * Résolution complète (rest §4.6) : mode de base = `palette` complétée par les valeurs Géode du mode ; autre mode = `paletteAlt` (clés absentes : valeurs Géode du
 * mode) sinon valeurs Géode du mode + `accent` de `palette` ; puis corrections. Sans `palette`, le thème Géode des deux modes.
 */
export const resolveModuleThemeColors = (input: ModuleThemeResolveInput): ResolvedModuleThemeColors => {
  const base: ModuleThemeScheme = input.baseScheme === 'dark' ? 'dark' : 'light';
  const other: ModuleThemeScheme = base === 'dark' ? 'light' : 'dark';
  const baseRaw = buildModeColors(base, input.palette ?? null);
  const otherRaw = input.paletteAlt
    ? buildModeColors(other, input.paletteAlt)
    : buildModeColors(other, input.palette ? { accent: input.palette.accent } : null);
  // Aligné sur le rest (rapport L14a rest, écart n° 1, tranché par le CORE le 20.10) : un mode SANS palette dans le document garde le thème Géode
  // tel quel, sans correction ni remarque ; la conformité AA des valeurs Géode elles-mêmes relève du design system, pas du module.
  const baseHasPalette = Boolean(input.palette);
  const otherHasPalette = Boolean(input.paletteAlt ?? input.palette);
  const baseFixed = baseHasPalette ? correctModeColors(base, baseRaw) : { colors: baseRaw, adjustments: [] };
  const otherFixed = otherHasPalette ? correctModeColors(other, otherRaw) : { colors: otherRaw, adjustments: [] };
  return {
    light: base === 'light' ? baseFixed.colors : otherFixed.colors,
    dark: base === 'dark' ? baseFixed.colors : otherFixed.colors,
    adjustments: [...baseFixed.adjustments, ...otherFixed.adjustments],
  };
};

/* ── Variables CSS ───────────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * LISTE FERMÉE des variables que le thème d'un module redéfinit (ordre fixe). Couleurs en triplets « T S% L% » ; les deux familles `--portal-*` et
 * `--color-*` / `--ui-surface-*` sont toutes deux redéfinies : une variable qui vaut `var(…)` est résolue sur l'élément qui la DÉCLARE (`:root`) puis héritée
 * résolue, redéfinir seulement `--ui-surface-3` sur l'enveloppe ne changerait pas `--portal-surface`.
 */
export const MODULE_THEME_VARIABLES: readonly string[] = [
  ...COMPONENT_RECIPE_VARIABLES,
  '--module-surface', '--module-surface-raised', '--module-ink', '--module-ink-muted', '--module-border', '--module-accent', '--module-accent-soft', '--module-on-accent',
  '--portal-bg',
  '--portal-surface',
  '--portal-surface-muted',
  '--portal-ink',
  '--portal-ink-muted',
  '--portal-border',
  '--portal-accent',
  '--portal-accent-soft',
  '--portal-ink-on-accent',
  '--color-background',
  '--color-surface',
  '--color-primary',
  '--color-primary-foreground',
  '--color-text-headline',
  '--color-text-body',
  '--color-text-label',
  '--color-border-subtle',
  '--ui-surface-1',
  '--ui-surface-2',
  '--ui-surface-3',
  '--radius-sm',
  '--radius-md',
  '--radius-lg',
  '--radius-xl',
  '--ui-shadow-soft',
  '--ui-shadow-floating',
  '--font-display',
  '--font-body',
  '--font-label',
];

type RadiusStep = Exclude<GeneratedModuleTheme['shape']['radius'], 'md'>;

/** Paliers de rayon (rest §4.5 n° 9) : `sm`, `md`, `lg`, `xl` du socle ; `md` n'est PAS redéfini. */
const RADIUS: Readonly<Record<RadiusStep, readonly [string, string, string, string]>> = {
  none: ['0', '0', '0', '0'],
  sm: ['0.125rem', '0.25rem', '0.375rem', '0.5rem'],
  lg: ['0.375rem', '0.625rem', '0.875rem', '1.125rem'],
  xl: ['0.5rem', '0.875rem', '1.25rem', '1.75rem'],
};

type ShadowStep = Exclude<GeneratedModuleTheme['shape']['shadow'], 'none'>;

/** Trois valeurs fixes du socle (doux, flottant) ; `{c}` = encre de l'ombre, `R G B`, calculée par `shadowInk`. */
const SHADOWS: Readonly<Record<ShadowStep, readonly [string, string]>> = {
  sm: ['0 1px 2px 0 rgb({c} / 0.1)', '0 4px 12px -2px rgb({c} / 0.14)'],
  md: ['0 4px 10px -4px rgb({c} / 0.16)', '0 12px 28px -6px rgb({c} / 0.22)'],
  lg: ['0 10px 24px -8px rgb({c} / 0.22)', '0 24px 48px -10px rgb({c} / 0.32)'],
};

/** Encre de l'ombre : la plus sombre de l'encre et de la surface du mode (jamais une couleur du document). */
const shadowInk = (colors: ModuleThemeColors): string => {
  const darker = luminanceOf(colors.ink) <= luminanceOf(colors.surface) ? colors.ink : colors.surface;
  return channelsOf(darker).join(' ');
};

const triplet = (hex: string): string => css_color_to_hsl_channels(hex, '0 0% 0%');

/** Nombre de couleurs du thème qui ne suivent pas `^#[0-9a-f]{6}$` (compteur de diagnostic `data-module-theme-invalid`). */
export const countInvalidModuleThemeColors = (theme: GeneratedModuleTheme, scheme: ModuleThemeScheme, override?: ModuleThemeOverride | null): number => {
  const colors = theme[scheme];
  let invalid = Object.values(colors ?? {}).filter((value) => !isModuleThemeHex(value)).length;
  if (!colors) invalid = 9;
  const accent = override ? (scheme === 'light' ? override.accentLight : override.accentDark) : null;
  if (accent !== null && accent !== undefined && !isModuleThemeHex(accent)) invalid += 1;
  return invalid;
};

/**
 * Variables CSS du thème pour un mode, ou `null` (aucune variable : thème du locataire) quand la surcharge du locataire est `tenant` ou quand une couleur
 * n'est pas à six chiffres hexadécimaux minuscules. Surcharge `module` avec accent de remplacement : `onAccent` et `accentSoft` sont recalculés puis les
 * règles (c) et (d) du rest §4.6 n° 3 s'appliquent (même fonction que le générateur).
 */
export function moduleThemeVariables(
  theme: GeneratedModuleTheme,
  scheme: ModuleThemeScheme,
  override?: ModuleThemeOverride | null
): Record<string, string> | null {
  if (override?.mode === 'tenant') return null;
  if (countInvalidModuleThemeColors(theme, scheme, override) > 0) return null;
  let colors = theme[scheme];
  const replacement = override ? (scheme === 'light' ? override.accentLight : override.accentDark) : null;
  if (replacement) {
    colors = applyModuleThemeAccent(theme, scheme, replacement).colors;
  }
  const variables: Record<string, string> = {
    '--portal-bg': triplet(colors.surface),
    '--portal-surface': triplet(colors.surfaceRaised),
    '--portal-surface-muted': triplet(colors.surfaceMuted),
    '--portal-ink': triplet(colors.ink),
    '--portal-ink-muted': triplet(colors.inkMuted),
    '--portal-border': triplet(colors.border),
    '--portal-accent': triplet(colors.accent),
    '--portal-accent-soft': triplet(colors.accentSoft),
    '--portal-ink-on-accent': triplet(colors.onAccent),
    '--color-background': triplet(colors.surface),
    '--color-surface': triplet(colors.surfaceRaised),
    '--color-primary': triplet(colors.accent),
    '--color-primary-foreground': triplet(colors.onAccent),
    '--color-text-headline': triplet(colors.ink),
    '--color-text-body': triplet(colors.ink),
    '--color-text-label': triplet(colors.inkMuted),
    '--color-border-subtle': triplet(colors.border),
    '--ui-surface-1': triplet(colors.surface),
    '--ui-surface-2': triplet(colors.surfaceMuted),
    '--ui-surface-3': triplet(colors.surfaceRaised),
  };
  const radius = theme.shape.radius;
  if (radius !== 'md' && radius in RADIUS) {
    const [sm, md, lg, xl] = RADIUS[radius as RadiusStep];
    variables['--radius-sm'] = sm;
    variables['--radius-md'] = md;
    variables['--radius-lg'] = lg;
    variables['--radius-xl'] = xl;
  }
  const shadow = theme.shape.shadow;
  if (shadow === 'none') {
    variables['--ui-shadow-soft'] = 'none';
    variables['--ui-shadow-floating'] = 'none';
  } else if (shadow in SHADOWS) {
    const ink = shadowInk(colors);
    const [soft, floating] = SHADOWS[shadow as ShadowStep];
    variables['--ui-shadow-soft'] = soft.replace('{c}', ink);
    variables['--ui-shadow-floating'] = floating.replace('{c}', ink);
  }
  if (theme.typography.heading) variables['--font-display'] = fontStack(theme.typography.heading.family);
  if (theme.typography.body) {
    variables['--font-body'] = fontStack(theme.typography.body.family);
    variables['--font-label'] = fontStack(theme.typography.body.family);
  }
  for(const key of ['surface','surfaceRaised','ink','inkMuted','border','accent','accentSoft','onAccent'] as const)variables['--module-'+key.replace(/[A-Z]/g,c=>'-'+c.toLowerCase())]=colors[key];
  Object.assign(variables, typographyVariables(theme.typography), componentRecipeVariables(theme.components));
  return variables;
}

/** Résolution partagée par l'application et le rapport de saisie L14d. */
export const applyModuleThemeAccent = (theme: GeneratedModuleTheme, scheme: ModuleThemeScheme, replacement: string) => {
  const colors = theme[scheme];
  return correctModeColors(scheme, { ...colors, accent: replacement, onAccent: bestInkFor([replacement]),
    accentSoft: mixHex(colors.surface, replacement, 0.14) }, ['c', 'd']);
};

/**
 * Pastille d'une couleur DONNÉE (palette d'un module, couleur mesurée dans un prototype) : style en ligne d'un aperçu d'outil de conception, jamais une couleur
 * d'interface. Vit ici (segment `/theme/`) pour que les écrans n'écrivent aucune couleur en ligne ; une valeur qui n'est pas `^#[0-9a-f]{6}$` ne donne aucun style.
 */
export const swatchStyle = (hex: string): CSSProperties | undefined => (isModuleThemeHex(hex) ? { backgroundColor: hex } : undefined);
